import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { useStytchWorkspaceMembers } from '@/hooks/useStytchWorkspaceMembers'
import { Member } from '@stytch/vanilla-js'
import {
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import {
	WORKSPACE_EDITOR_ROLE,
	WORKSPACE_OWNER_ROLE,
	WORKSPACE_VIEWER_ROLE,
} from 'honeygrid-types/validators-and-types/StytchWorkspaceTypes'
import { MoreHorizontal } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import type { ColumnDef, SortingState } from '@tanstack/react-table'
import type { WorkspaceRole } from 'honeygrid-types'

type UserRow = {
	id: string
	name: string
	email: string
	role: WorkspaceRole
	invitation_status: 'accepted' | 'pending'
	joined_at: string
	member_id: string
}

const formatDate = (date: string | null) => {
	if (!date) return '—'
	return new Date(date).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})
}

const getRoleColor = (role: WorkspaceRole) => {
	switch (role) {
		case 'owner':
			return 'text-primary'
		case 'editor':
			return 'text-blue-500'
		case 'viewer':
			return 'text-muted-foreground'
	}
}

const getStatusColor = (status: UserRow['invitation_status']) => {
	switch (status) {
		case 'accepted':
			return 'text-green-500'
		case 'pending':
			return 'text-yellow-500'
	}
}

interface UserListProps {
	members: Member[]
	isLoading: boolean
}

export const UserList = ({ members, isLoading }: UserListProps) => {
	const { workspaceId = '' } = useParams<{ workspaceId: string }>()
	const { updateRole, removeMember } = useStytchWorkspaceMembers()
	const [sorting, setSorting] = useState<SortingState>([{ id: 'joined_at', desc: true }])

	// Map Stytch roles to workspace roles
	const mapStytchRoleToWorkspaceRole = (stytchRoles: Array<{ role_id: string }>): WorkspaceRole => {
		if (stytchRoles.some((role) => role.role_id === WORKSPACE_OWNER_ROLE)) {
			return 'owner'
		}
		if (stytchRoles.some((role) => role.role_id === WORKSPACE_EDITOR_ROLE)) {
			return 'editor'
		}
		return 'viewer'
	}

	// Map Stytch status to invitation status
	const mapStytchStatusToInvitationStatus = (status: string) => {
		switch (status) {
			case 'active':
				return 'accepted'
			case 'pending':
				return 'pending'
			default:
				return 'pending'
		}
	}

	const currentUser = members?.find(
		(member: any) =>
			member.status === 'active' &&
			member.roles?.some((role: { role_id: string }) => role.role_id === WORKSPACE_OWNER_ROLE)
	)
	const isCurrentUserOwner = Boolean(currentUser)
	const numActiveOwners =
		members?.filter(
			(member: any) =>
				member.status === 'active' &&
				member.roles?.some((role: { role_id: string }) => role.role_id === WORKSPACE_OWNER_ROLE)
		).length || 0

	const rows: UserRow[] = useMemo(() => {
		if (!members) return []
		return members.map((member) => ({
			id: member.member_id,
			member_id: member.member_id,
			name: member.name || '—',
			email: member.email_address,
			role: mapStytchRoleToWorkspaceRole(member.roles || []),
			invitation_status: mapStytchStatusToInvitationStatus(member.status),
			joined_at: member.created_at,
		}))
	}, [members])

	const columns: Array<ColumnDef<UserRow>> = useMemo(
		() => [
			{
				accessorKey: 'name',
				header: 'Name',
				cell: ({ row }) => <div className="font-medium">{row.getValue('name')}</div>,
			},
			{
				accessorKey: 'email',
				header: 'Email',
				cell: ({ row }) => (
					<code className="text-sm text-muted-foreground">{row.getValue('email') || '—'}</code>
				),
			},
			{
				accessorKey: 'role',
				header: 'Role',
				cell: ({ row }) => {
					const role = row.getValue('role') as WorkspaceRole
					return <span className={`font-medium capitalize ${getRoleColor(role)}`}>{role}</span>
				},
			},
			{
				accessorKey: 'invitation_status',
				header: 'Status',
				cell: ({ row }) => {
					const status = row.getValue('invitation_status') as UserRow['invitation_status']
					return (
						<div className="flex items-center gap-2">
							<div
								className={`h-2 w-2 rounded-full ${
									status === 'accepted' ? 'bg-green-500' : 'bg-yellow-500'
								}`}
							/>
							<span className={`font-medium capitalize ${getStatusColor(status)}`}>{status}</span>
						</div>
					)
				},
			},
			{
				accessorKey: 'joined_at',
				header: 'Joined',
				cell: ({ row }) => formatDate(row.getValue('joined_at')),
			},
			{
				id: 'actions',
				cell: ({ row }) => {
					const user = row.original
					const isActiveOwner = user.role === 'owner' && user.invitation_status === 'accepted'

					const handleRemoveUser = async () => {
						try {
							await removeMember(user.member_id)
							toast.success('User removed', {
								description: `Successfully removed ${user.email} from the workspace`,
							})
						} catch (error) {
							console.error(error)
							toast.error('Failed to remove user', {
								description: 'Please try again.',
							})
						}
					}

					const handleRoleChange = async (newRole: WorkspaceRole) => {
						try {
							// Map workspace role to Stytch role
							const stytchRole =
								newRole === 'owner'
									? WORKSPACE_OWNER_ROLE
									: newRole === 'editor'
										? WORKSPACE_EDITOR_ROLE
										: WORKSPACE_VIEWER_ROLE

							await updateRole({ member_id: user.member_id, roles: [stytchRole] })
							toast.success('Role updated', {
								description: `Successfully updated ${user.email || 'user'}'s role to ${newRole}`,
							})
						} catch (error) {
							toast.error('Failed to update role', {
								description: 'Please try again.',
							})
						}
					}

					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="h-8 w-8 p-0"
									disabled={isActiveOwner && numActiveOwners < 2}
								>
									<span className="sr-only">Open menu</span>
									<MoreHorizontal className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuSub>
									<DropdownMenuSubTrigger
										className="flex items-center"
										disabled={isActiveOwner && numActiveOwners < 2}
									>
										Change Role
									</DropdownMenuSubTrigger>
									<DropdownMenuSubContent>
										{isCurrentUserOwner && (
											<DropdownMenuItem
												disabled={user.role === 'owner'}
												onClick={() => handleRoleChange('owner')}
											>
												<span className={getRoleColor('owner')}>Owner</span>
											</DropdownMenuItem>
										)}
										<DropdownMenuItem
											disabled={user.role === 'editor'}
											onClick={() => handleRoleChange('editor')}
										>
											<span className={getRoleColor('editor')}>Editor</span>
										</DropdownMenuItem>
										<DropdownMenuItem
											disabled={user.role === 'viewer'}
											onClick={() => handleRoleChange('viewer')}
										>
											<span className={getRoleColor('viewer')}>Viewer</span>
										</DropdownMenuItem>
									</DropdownMenuSubContent>
								</DropdownMenuSub>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									disabled={isActiveOwner && numActiveOwners < 2}
									className="text-red-600"
									onClick={handleRemoveUser}
								>
									Remove User
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
			},
		],
		[workspaceId, removeMember, updateRole, isCurrentUserOwner]
	)

	const table = useReactTable({
		data: rows,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		state: {
			sorting,
		},
	})

	if (isLoading) {
		return <div>Loading...</div>
	}

	return (
		<div className="rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<TableHead key={header.id}>
									{header.isPlaceholder
										? null
										: flexRender(header.column.columnDef.header, header.getContext())}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map((row) => (
							<TableRow key={row.original.id}>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-24 text-center">
								No results.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	)
}
